<template>
  <widget
    :header="widgetData.header"
    :color="widgetData.headerColor"
    closable
    @close="$emit('close')"
  >
    <div
      v-if="widgetData.phoneNumberVisible"
      class="click-to-text-widget-expanded__number"
      :style="{ color: widgetData.phoneNumberColor }"
    >
      {{ widgetPhoneNumber }}
    </div>

    <div class="click-to-text-widget-expanded__description">
      {{ widgetData.description }}
    </div>

    <div class="flex flex-nowrap column gap-2">
      <widget-form-line
        v-if="widgetData.nameVisible"
        :label="widgetData.nameLabel"
      >
        <widget-form-field-text
          name="name"
          :placeholder="$t('clickToTextWidgetExpanded.name')"
          :max-length="101"
        />
      </widget-form-line>

      <widget-form-line :label="widgetData.phoneNumberLabel">
        <widget-form-field-phone
          country-name="countryCode"
          phone-name="phoneNumber"
          :available-countries="widgetData.countries"
        />
      </widget-form-line>

      <widget-form-line :label="widgetData.messageLabel">
        <widget-form-field-text
          name="message"
          :placeholder="$t('clickToTextWidgetExpanded.message')"
          :max-length="720"
        />
      </widget-form-line>
    </div>

    <widget-button
      class="w100pr mt-4"
      size="36"
      :color="buttonColor"
      @click="$emit('submit')"
    >
      <span class="subhead-semi-bold-16 break-spaces break-word pt-2px pb-2px">
        {{ widgetData.submitText }}
      </span>
    </widget-button>
  </widget>
</template>

<script lang="ts">
import WidgetFormLine from '@widgets/shared/components/WidgetFormLine.vue'
import Widget from '@widgets/shared/components/Widget.vue'
import WidgetFormFieldText from '@widgets/shared/components/fields/WidgetFormFieldText.vue'
import WidgetButton from '@widgets/shared/components/WidgetButton.vue'
import { phoneService } from '@widgets/shared/services/phoneService'
import WidgetFormFieldPhone from '@widgets/shared/components/fields/WidgetFormFieldPhone.vue'
import type { WidgetButtonCustomColor } from '@widgets/shared/types'
import type { PropType } from '@/composition/vue/compositionApi'
import { computed, defineComponent } from '@/composition/vue/compositionApi'
import type { ClickToTextForm } from '@/services/domain/services/forms/clickToText/types'
import { styleVars } from '@/constants/styleVariables'

export default defineComponent({
  components: {
    WidgetFormFieldPhone,
    WidgetFormFieldText,
    Widget,
    WidgetButton,
    WidgetFormLine,
  },
  props: {
    widgetData: {
      type: Object as PropType<ClickToTextForm>,
      required: true,
    },
  },
  emits: {
    submit: () => true,
    close: () => true,
  },
  setup(props) {
    const widgetPhoneNumber = computed(() => phoneService.format(props.widgetData.phoneNumber))
    const buttonColor = computed<WidgetButtonCustomColor>(() => ({
      primary: props.widgetData.submitColor,
      secondary: styleVars.white,
    }))

    return {
      widgetPhoneNumber,
      buttonColor,
    }
  },
})
</script>

<style lang="scss" scoped>
.click-to-text-widget-expanded {
  &__number {
    font-size: $xxl-font;
    font-weight: $bold-font;
    text-align: center;
    line-height: 38px;
    margin-bottom: 8px;

    @include media-sm-max {
      margin-left: -6px;
      margin-right: -6px;
    }
  }

  &__description {
    font-weight: $semi-bold-font;
    line-height: 20px;
    color: $neutral;
    margin-bottom: 24px;
    text-align: center;
    word-break: break-word;
  }
}
</style>
